import { useReducer, useCallback } from 'react';
import { topUpReducer, initialTopUpState } from '../reducer/topup.reducer';
import { topUpActions } from '../action/topup.action';
import { createTopUpPayment } from '../api/topupApi';
import { TopUpPaymentResponse } from '../model/topup.types';
import { CardElement, useStripe, useElements } from '@stripe/react-stripe-js';
export const useTopUpViewModel = (
    onSubmit: (amount: number) => void,

) => {
    const [state, dispatch] = useReducer(topUpReducer, initialTopUpState);
    const stripe = useStripe();
    const elements = useElements();

    const handleAmountChange = useCallback((value: string) => {
        dispatch(topUpActions.setAmount(value));
    }, []);

    const validateAmount = useCallback((amount: string): boolean => {
        const numericAmount = Number(amount);

        if (!amount || isNaN(numericAmount) || numericAmount <= 0) {
            dispatch(topUpActions.setError('Please enter a valid amount'));
            return false;
        }

        return true;
    }, []);

    const handleSubmit = useCallback(async () => {
        if (!validateAmount(state.amount)) {
            return;
        }

        const numericAmount = Number(state.amount);

        try {
            dispatch(topUpActions.submitStart());

            // Call the Stripe top-up payment API
            const response: TopUpPaymentResponse = await createTopUpPayment(numericAmount);

            console.log('Top-up payment response:', response);

            // If payment URL is returned, redirect to Stripe checkout
            if (response.client_secret && response.payment_intent_id) {
                // window.location.href = response.client_secret;

                if (!stripe || !elements) {
                    dispatch(topUpActions.setError('Stripe is not ready yet'));
                    return;
                }

                const cardElement = elements.getElement(CardElement);

                if (!cardElement) {
                    dispatch(topUpActions.setError('Card element is not available'));
                    return;
                }

                const { error: stripeError, paymentIntent } = await stripe.confirmCardPayment(
                    response.client_secret,
                    {
                        payment_method: {
                           card: cardElement
                        },
                    }
                );

                if (stripeError) {
                    dispatch(topUpActions.setError(stripeError.message || 'Payment failed'));
                } else if (paymentIntent.status === 'succeeded') {
                    // Payment successful - credits will be added via webhook
                    alert('Payment successful! Your account will be credited shortly.');
                    dispatch(topUpActions.submitSuccess());
                    onSubmit(numericAmount);
                } else {
                    dispatch(topUpActions.setError('Payment was not completed. Please try again.'));
                }
            } else {
                dispatch(topUpActions.setError('Invalid payment response from server'));
            }

        } catch (error: any) {
            const errorMessage =
                error.message || 'Failed to process top-up payment';

            dispatch(topUpActions.setError(errorMessage));
            console.error('Top-up payment error:', error);
        }
    }, [state.amount, onSubmit, validateAmount, stripe, elements]);

    const handleReset = useCallback(() => {
        dispatch(topUpActions.reset());
    }, []);

    return {
        state,
        handleAmountChange,
        handleSubmit,
        handleReset,
    };
};
